import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { positionEnableDisable } from '@/api/domains/organization/positions';
import { useUI } from '@/hooks/ui/useUI';


interface UsePositionEnableDisableResult {
  enablePosition: (positionId: number) => Promise<boolean>;
  disablePosition: (positionId: number) => Promise<boolean>;
  isEnabling: boolean;
  isDisabling: boolean;
  enablingIds: Set<number>;
  disablingIds: Set<number>;
}

export function usePositionEnableDisable(): UsePositionEnableDisableResult {
  const { t: translate } = useTranslation();
  const { showNotification } = useUI();
  const [isEnabling, setIsEnabling] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [enablingIds, setEnablingIds] = useState<Set<number>>(new Set());
  const [disablingIds, setDisablingIds] = useState<Set<number>>(new Set());

  const enablePosition = useCallback(async (positionId: number): Promise<boolean> => {
    setIsEnabling(true);
    setEnablingIds((previous) => new Set(previous).add(positionId));

    const response = await positionEnableDisable.enable(positionId);

    setEnablingIds((previous) => {
      const next = new Set(previous);
      next.delete(positionId);
      return next;
    });
    setIsEnabling(false);

    if (response.success) {
      showNotification('success', translate('organization:messages.positionEnabled'));
      return true;
    }

    showNotification(
      'error',
      response.error?.detail || translate('organization:messages.positionEnableError'),
    );
    return false;
  }, [showNotification, translate]);

  const disablePosition = useCallback(async (positionId: number): Promise<boolean> => {
    setIsDisabling(true);
    setDisablingIds((previous) => new Set(previous).add(positionId));

    const response = await positionEnableDisable.disable(positionId);

    setDisablingIds((previous) => {
      const next = new Set(previous);
      next.delete(positionId);
      return next;
    });
    setIsDisabling(false);

    if (response.success) {
      showNotification('success', translate('organization:messages.positionDisabled'));
      return true;
    }

    showNotification(
      'error',
      response.error?.detail || translate('organization:messages.positionDisableError'),
    );
    return false;
  }, [showNotification, translate]);

  return {
    enablePosition,
    disablePosition,
    isEnabling,
    isDisabling,
    enablingIds,
    disablingIds,
  };
}

export default usePositionEnableDisable;
